import { useMemo, useState } from 'react';
import { CheckCircle2, Link2, Loader2, X } from 'lucide-react';
import { NoteDisclosure } from '@/components/Disclosure';
import { Hint } from '@/components/Hint';
import { StatusPill } from '@/components/Status';
import { type StatusTone } from '@/lib/status';
import {
  attachPackageToOrder,
  setDiscrepancyDispositions,
  type OrderSummary,
  type OrdersOverview,
  type PendingResolution,
  type OrderPackage,
  type UnlinkedPackage,
} from '@/api/orders';
import { isOpenProblem, setPackageNoJourneyReason } from '@/api/packages';
import { fmtDate, money, pkgStatusLabel } from './lib';
import { Can } from '@/auth/guards';
import { PickJourneyDialog } from './PickJourneyDialog';
import { ResolveProblemDialog } from './ResolveProblemDialog';
import { SourceEmailLink } from './SourceEmailLink';
import {
  AcotReceivingLink, JourneyName, PendingNudge, RowAction, VendorDraftButton,
  WorkEmpty, WorkRow, WorkSection,
} from './workLib';
import { taskLabel } from './workUtils';

/** Advance or take back one watched resolution, preserving what was recorded. */
async function repost(
  r: PendingResolution,
  changes: { resolution_status?: 'pending' | 'requested' | 'resolved'; disposition?: null }
) {
  await setDiscrepancyDispositions(r.receiptId, [{
    line_key: r.lineKey,
    pid: r.pid,
    ...(changes.disposition === null
      ? { disposition: null }
      : {
        disposition: r.disposition,
        credit_amount: r.creditAmount,
        note: r.note,
        ...(changes.resolution_status ? { resolution_status: changes.resolution_status } : {}),
      }),
  }]);
}

/** The credit-claim draft's body (plain text; Mail threads it as a reply). */
function claimEmailText(r: PendingResolution): string {
  return [
    'Hi,',
    '',
    `Reconciling our recent order${r.label ? ` (${r.label})` : ''} we came up short against the invoice`
      + `${r.creditAmount != null ? ` by ${money(r.creditAmount, r.currency)}` : ''}. `
      + 'Could you issue a credit for the missing units?',
    ...(r.note ? ['', `Details: ${r.note}`] : []),
    '',
    'Thanks!',
  ].join('\n');
}

/** An unlinked parcel's own status, toned as the journey rollup tones it. */
function parcelTone(pkg: UnlinkedPackage): StatusTone {
  if (isOpenProblem(pkg)) return 'danger';
  if (pkg.status === 'delivered') return 'success';
  if (['in_transit', 'out_for_delivery', 'available_for_pickup'].includes(pkg.status)) return 'info';
  return 'neutral';
}

/** The agent's note on a decision — on request, in the row's detail slot. */
function resolutionNote(note: string | null) {
  if (!note) return undefined;
  return <NoteDisclosure text={note} />;
}

/**
 * Two honest piles. NEEDS YOU: delivery problems and credit claims not yet
 * sent. WATCHING: things someone else finishes (credits in flight, recaptures
 * with the orders run, queued nudges) — visible so silence never reads as
 * loss, gone the moment the run observes the fix.
 *
 * Recounts are not here: they stay in the Discrepancies table until resolved
 * or let go, and a second listing only doubled them. Neither are verify_setup
 * nudges — "I set these up" is Matt's own report for the run to confirm, not
 * something he is waiting on; it stays on its journey.
 */
export function FollowUpsView({ overview, orders, onChanged, onOpenJourney }: {
  overview: OrdersOverview;
  orders: OrderSummary[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [attaching, setAttaching] = useState<UnlinkedPackage | null>(null);
  const [resolving, setResolving] = useState<OrderPackage | null>(null);

  const problems = useMemo(
    () => orders.flatMap((o) => o.packages
      .filter(isOpenProblem)
      .map((p) => ({ order: o, pkg: p }))),
    [orders]
  );
  const unlinked = overview.unlinkedPackages ?? [];
  const creditsToSend = useMemo(
    () => overview.resolutions.filter((r) => r.disposition === 'credited' && r.resolutionStatus !== 'requested'),
    [overview]
  );
  const creditsWatched = useMemo(
    () => overview.resolutions.filter((r) => r.disposition === 'credited' && r.resolutionStatus === 'requested'),
    [overview]
  );
  const recaptures = useMemo(
    () => overview.resolutions.filter((r) => r.disposition === 'recapture'),
    [overview]
  );
  const nudges = useMemo(
    () => overview.tasks.filter((t) => t.kind !== 'verify_setup'),
    [overview]
  );

  const act = async (key: string, fn: () => Promise<void>) => {
    setBusyKey(key);
    try { await fn(); onChanged(); } finally { setBusyKey(null); }
  };

  const takeBack = (r: PendingResolution) => {
    const key = `${r.receiptId}:${r.lineKey}`;
    return busyKey === key
      ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      : (
        <Can code="orders:write:balance">
          <Hint content="Take the decision back.">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => act(key, () => repost(r, { disposition: null }))}
            >
              <X className="h-3 w-3" />
            </button>
          </Hint>
        </Can>
      );
  };

  // The journey's vendor contact, by receipt — who a claim draft replies to.
  const vendorEmailByReceipt = useMemo(
    () => new Map(overview.receivings.map((r) => [r.receiptId, r.vendorEmail])),
    [overview.receivings]
  );

  const needsYou = problems.length + unlinked.length + creditsToSend.length;
  const watching = creditsWatched.length + recaptures.length + nudges.length;

  if (needsYou === 0 && watching === 0) {
    return <WorkEmpty>Nothing in flight and nothing being watched.</WorkEmpty>;
  }

  const tracking = (pkg: { publicUrl?: string | null; carrier?: string | null; trackingCode: string }) => pkg.publicUrl ? (
    <a
      href={pkg.publicUrl}
      target="_blank"
      rel="noreferrer"
      className="text-xs tabular-nums text-primary hover:underline"
    >
      {pkg.carrier} {pkg.trackingCode}
    </a>
  ) : (
    <span className="text-xs tabular-nums text-muted-foreground">{pkg.carrier} {pkg.trackingCode}</span>
  );

  return (
    <div>
      {unlinked.length > 0 && (
        <WorkSection title="Parcels with no journey" count={unlinked.length}>
          {unlinked.map((pkg) => {
            const key = `unlinked:${pkg.id}`;
            const busy = busyKey === key;
            return (
              <WorkRow
                key={pkg.id}
                scrollKey={key}
                title={<span className="font-semibold">{pkg.vendor || 'Unlabelled shipper'}</span>}
                actions={
                  <>
                    <Can code="orders:write:journeys">
                      <Hint content="Link this parcel to a journey.">
                        <RowAction
                          icon={<Link2 className="h-3 w-3" />}
                          busy={busy}
                          disabled={busy}
                          onClick={() => setAttaching(pkg)}
                        >
                          Attach to journey
                        </RowAction>
                      </Hint>
                    </Can>
                    <Can code="orders:write:packages">
                      <Hint content="Not an order: a sample or a replacement. It stays tracked.">
                        <RowAction
                          variant="quiet"
                          disabled={busy}
                          onClick={() => act(key, async () => {
                            await setPackageNoJourneyReason(pkg.id, 'Not an order — marked from the Orders page');
                          })}
                        >
                          Not an order
                        </RowAction>
                      </Hint>
                    </Can>
                  </>
                }
              >
                {pkg.orderRef && (
                  <span className="text-xs text-muted-foreground">ref {pkg.orderRef}</span>
                )}
                {tracking(pkg)}
                <StatusPill tone={parcelTone(pkg)}>
                  {pkgStatusLabel(pkg.status)}
                  {pkg.deliveredAt ? ` ${fmtDate(pkg.deliveredAt)}` : ''}
                </StatusPill>
                {pkg.messageRefs?.[0] && (
                  <SourceEmailLink
                    messageId={pkg.messageRefs[0].message_id}
                    label="source email"
                    hint="Open the email this tracking number came from"
                  />
                )}
              </WorkRow>
            );
          })}
        </WorkSection>
      )}

      {problems.length > 0 && (
        <WorkSection title="Delivery problems" count={problems.length}>
          {problems.map(({ order, pkg }) => (
            <WorkRow
              key={pkg.id}
              scrollKey={`problem:${pkg.id}`}
              title={<JourneyName vendor={order.vendor} label={order.label} onOpen={() => onOpenJourney(order.id)} />}
              tone="danger"
              actions={
                <Can code="orders:write:packages">
                  <Hint content="Settled: record how. It stays on the journey.">
                    <RowAction
                      icon={<CheckCircle2 className="h-3 w-3" />}
                      onClick={() => setResolving(pkg)}
                    >
                      Resolved
                    </RowAction>
                  </Hint>
                </Can>
              }
            >
              <StatusPill tone="danger">{pkgStatusLabel(pkg.status)}</StatusPill>
              {pkg.statusDetail && <span className="text-xs text-muted-foreground">{pkg.statusDetail}</span>}
              {tracking(pkg)}
            </WorkRow>
          ))}
        </WorkSection>
      )}

      {creditsToSend.length > 0 && (
        <WorkSection title="Credit claims to send" count={creditsToSend.length}>
          {creditsToSend.map((r) => {
            const key = `${r.receiptId}:${r.lineKey}`;
            return (
              <WorkRow
                key={key}
                scrollKey={`credit:${key}`}
                title={<JourneyName vendor={r.vendor} label={r.label} onOpen={() => onOpenJourney(r.orderId)} />}
                detail={resolutionNote(r.note)}
                actions={
                  <>
                    {/* The claim draft is a balance write server-side, like
                        its second half, Mark requested. */}
                    <Can code="orders:write:balance">
                      <VendorDraftButton
                        orderId={r.orderId}
                        vendorEmail={vendorEmailByReceipt.get(r.receiptId) ?? null}
                        purpose="claim"
                        text={claimEmailText(r)}
                        label="Draft claim"
                        variant="primary"
                      />
                    </Can>
                    <Can code="orders:write:balance">
                      <Hint content="Already asked; moves it to Watching.">
                        <RowAction
                          variant="quiet"
                          disabled={busyKey === key}
                          onClick={() => act(key, () => repost(r, { resolution_status: 'requested' }))}
                        >
                          Mark requested
                        </RowAction>
                      </Hint>
                    </Can>
                    {takeBack(r)}
                  </>
                }
              >
                <AcotReceivingLink receivingId={r.receivingId} />
                {r.creditAmount != null && (
                  <span className="text-xs font-medium tabular-nums">{money(r.creditAmount, r.currency)}</span>
                )}
              </WorkRow>
            );
          })}
        </WorkSection>
      )}

      {watching > 0 && (
        <WorkSection title="Watching" count={watching}>
          {creditsWatched.map((r) => {
            const key = `${r.receiptId}:${r.lineKey}`;
            return (
              <WorkRow
                key={key}
                scrollKey={`watch:${key}`}
                title={<JourneyName vendor={r.vendor} label={r.label} onOpen={() => onOpenJourney(r.orderId)} />}
                actions={
                  <>
                    <Can code="orders:write:balance">
                      <Hint content="The credit arrived.">
                        <RowAction
                          disabled={busyKey === key}
                          onClick={() => act(key, () => repost(r, { resolution_status: 'resolved' }))}
                        >
                          Credit landed
                        </RowAction>
                      </Hint>
                    </Can>
                    {takeBack(r)}
                  </>
                }
              >
                <AcotReceivingLink receivingId={r.receivingId} />
                {r.creditAmount != null && (
                  <span className="text-xs tabular-nums">{money(r.creditAmount, r.currency)}</span>
                )}
              </WorkRow>
            );
          })}

          {recaptures.map((r) => {
            const key = `${r.receiptId}:${r.lineKey}`;
            return (
              <WorkRow
                key={key}
                scrollKey={`scan:${key}`}
                title={<JourneyName vendor={r.vendor} label={r.label} onOpen={() => onOpenJourney(r.orderId)} />}
                detail={resolutionNote(r.note)}
                actions={takeBack(r)}
              >
                <AcotReceivingLink receivingId={r.receivingId} />
                {r.updatedAt && <span className="text-xs text-muted-foreground tabular-nums">since {fmtDate(r.updatedAt)}</span>}
              </WorkRow>
            );
          })}

          {nudges.map((task) => (
            <WorkRow
              key={task.id}
              scrollKey={`task:${task.id}`}
              title={<JourneyName vendor={task.vendor} label={task.label} onOpen={() => onOpenJourney(task.orderId)} />}
              actions={<PendingNudge task={task} onWithdrawn={onChanged} />}
            >
              <span className="text-xs text-muted-foreground">
                {taskLabel(task.kind)}
                {typeof task.payload.filename === 'string' && ` — ${task.payload.filename}`}
              </span>
            </WorkRow>
          ))}
        </WorkSection>
      )}

      <PickJourneyDialog
        open={attaching != null}
        onOpenChange={(open: boolean) => { if (!open) setAttaching(null); }}
        vendorGuess={attaching?.vendor}
        title="Attach parcel to journey"
        description={attaching ? `Links ${[attaching.carrier, attaching.trackingCode].filter(Boolean).join(' ')} to the journey.` : ''}
        successNote="The parcel is linked."
        onPick={async (order: OrderSummary) => {
          if (!attaching) return;
          await attachPackageToOrder(order.id, {
            trackingCode: attaching.trackingCode,
            carrier: attaching.carrier,
            vendor: attaching.vendor,
          });
          onChanged();
        }}
      />
      <ResolveProblemDialog
        pkg={resolving}
        onOpenChange={(open) => { if (!open) setResolving(null); }}
        onResolved={onChanged}
      />
    </div>
  );
}
